<template>
  <b-tab title="Rapport PDF">
    <div>
      <b-card>
        <b-card-header>
          <h4>Introduction du rapport</h4>
        </b-card-header>
        <b-card-body>
          <b-form-textarea
            v-model="textePreliminaire"
            placeholder="Introduire le texte préliminaire"
            rows="4"
            max-rows="8"
            required
          />
        </b-card-body>
      </b-card>
    </div>

    <div>
      <b-card class="cardelement">
        <h3>Cover du Rapport</h3>
        <b-container fluid>
          <div v-if="cover">
            <p><strong>{{ cover.filename }}</strong></p>
            <p><strong>Date d'ajout :</strong> {{ cover.added.toLocaleString() }}</p>
            <div class="d-flex gap-2">
              <b-button size="sm" variant="primary" @click="triggerCoverUpload">
                Modifier / Remplacer
              </b-button>
              <b-button size="sm" variant="danger" @click="deleteCover">
                Supprimer
              </b-button>
            </div>
          </div>
          <div v-else>
            <em class="text-muted">Pas de Cover PDF enregistré.</em>
            <b-button size="sm" variant="primary" class="mt-2" @click="triggerCoverUpload">
              Télécharger un Cover PDF
            </b-button>
          </div>

          <input ref="coverFileInput" type="file" accept=".pdf" style="display: none;" @change="handleCoverFileChange" />

          <div v-if="uploadingCover" class="mt-3 text-center">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">Upload du Cover PDF en cours...</p>
          </div>

          <div v-if="coverFile && !uploadingCover" class="mt-2">
            <p><strong>Fichier sélectionné :</strong> {{ coverFile.name }}</p>
          </div>
        </b-container>
      </b-card>
    </div>

    <div>
      <b-card class="cardelement">
        <h3>Notice du Rapport</h3>
        <b-container fluid>
          <div v-if="notice">
            <p><strong>{{ notice.filename }}</strong></p>
            <p><strong>Date d'ajout :</strong> {{ notice.added.toLocaleString() }}</p>
            <div class="d-flex gap-2">
              <b-button size="sm" variant="primary" @click="triggerNoticeUpload">
                Modifier / Remplacer
              </b-button>
              <b-button size="sm" variant="danger" @click="deleteNotice">
                Supprimer
              </b-button>
            </div>
          </div>
          <div v-else>
            <em class="text-muted">Pas de Notice PDF enregistré.</em>
            <b-button size="sm" variant="primary" class="mt-2" @click="triggerNoticeUpload">
              Télécharger une Notice PDF
            </b-button>
          </div>

          <input ref="noticeFileInput" type="file" accept=".pdf" style="display: none;" @change="handleNoticeFileChange" />

          <div v-if="uploadingNotice" class="mt-3 text-center">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">Upload de la Notice PDF en cours...</p>
          </div>

          <div v-if="noticeFile && !uploadingNotice" class="mt-2">
            <p><strong>Fichier sélectionné :</strong> {{ noticeFile.name }}</p>
          </div>
        </b-container>
      </b-card>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <b-button variant="primary" size="lg" @click="saveReport">Enregistrer</b-button>
    </div>
  </b-tab>
</template>

<script>
export default {
  name: "TestReportPdf",
  props: {
    test: { type: Object, required: true }
  },
  data() {
    return {
      textePreliminaire: '',
      cover: null,
      notice: null,
      coverFile: null,
      noticeFile: null,
      uploadingCover: false,
      uploadingNotice: false,
    };
  },
  methods: {
    triggerCoverUpload() { this.$refs.coverFileInput.click(); },
    handleCoverFileChange(event) {
      this.coverFile = event.target.files[0];
      this.uploadCoverFile();
    },
    async uploadCoverFile() {
      if (!this.coverFile || !this.test.id) return;
      this.uploadingCover = true;
      const formData = new FormData();
      formData.append("pdf_cover", this.coverFile);
      try {
        const res = await fetch(`/api/tests/${this.test.id}/fichiers-envoi`, { method: "POST", body: formData });
        const data = await res.json();
        if (res.ok) {
          this.cover = { filename: this.coverFile.name, url: `/api/files/covers/${data.params.pdf_cover}`, added: new Date() };
          this.$bvToast?.toast("Cover PDF mis à jour avec succès", { title: "Succès", variant: "success", solid: true });
        }
      } catch (err) {
        console.error(err);
        this.$bvToast?.toast("Erreur lors de l'upload du Cover PDF", { title: "Erreur", variant: "danger", solid: true });
      } finally {
        this.uploadingCover = false;
      }
    },

    triggerNoticeUpload() { this.$refs.noticeFileInput.click(); },
    handleNoticeFileChange(event) {
      this.noticeFile = event.target.files[0];
      this.uploadNoticeFile();
    },
    async uploadNoticeFile() {
      if (!this.noticeFile || !this.test.id) return;
      this.uploadingNotice = true;
      const formData = new FormData();
      formData.append("pdf_notice", this.noticeFile);
      try {
        const res = await fetch(`/api/tests/${this.test.id}/fichiers-envoi`, { method: "POST", body: formData });
        if (res.ok) {
          this.notice = { filename: this.noticeFile.name, url: `/api/files/notices/${this.test.id}_notice.pdf`, added: new Date() };
          this.$bvToast?.toast("Notice PDF mise à jour avec succès", { title: "Succès", variant: "success", solid: true });
        }
      } catch (err) {
        console.error(err);
        this.$bvToast?.toast("Erreur lors de l'upload de la Notice PDF", { title: "Erreur", variant: "danger", solid: true });
      } finally {
        this.uploadingNotice = false;
      }
    },

    async fetchReportParams() {
      if (!this.test.id) return;
      try {
        const res = await fetch(`/api/tests/${this.test.id}/pdf-params`);
        if (res.ok) {
          const data = await res.json();
          this.textePreliminaire = data.pdf_intro || "";
          this.cover = data.pdf_cover ? { filename: data.pdf_cover, url: `/api/files/covers/${data.pdf_cover}`, added: new Date() } : null;
          this.notice = data.pdf_notice ? { filename: data.pdf_notice, url: `/api/files/notices/${data.pdf_notice}`, added: new Date() } : null;
        }
      } catch (e) {
        this.textePreliminaire = "";
        this.cover = null;
        this.notice = null;
      }
    },

    async saveReport() {
      if (!this.test.id) return;
      const payload = { pdf_intro: this.textePreliminaire };
      try {
        const res = await fetch(`/api/tests/${this.test.id}/pdf-params`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          this.$bvToast?.toast("Texte préliminaire enregistré avec succès", { title: "Succès", variant: "success", solid: true });
          await this.fetchReportParams();
        }
      } catch (e) {
        this.$bvToast?.toast("Erreur lors de l'enregistrement du texte", { title: "Erreur", variant: "danger", solid: true });
      }
    },

    async deleteCover() {
      if (!this.test.id) return;
      try {
        const res = await fetch(`/api/tests/${this.test.id}/files/cover`, { method: "DELETE" });
        if (res.ok) {
          this.cover = null;
          this.$bvToast?.toast("Cover supprimé avec succès", { title: "Succès", variant: "success", solid: true });
        }
      } catch (err) {
        console.error(err);
        this.$bvToast?.toast("Erreur suppression cover", { title: "Erreur", variant: "danger", solid: true });
      }
    },

    async deleteNotice() {
      if (!this.test.id) return;
      try {
        const res = await fetch(`/api/tests/${this.test.id}/files/notice`, { method: "DELETE" });
        if (res.ok) {
          this.notice = null;
          this.$bvToast?.toast("Notice supprimée avec succès", { title: "Succès", variant: "success", solid: true });
        }
      } catch (err) {
        console.error(err);
        this.$bvToast?.toast("Erreur suppression notice", { title: "Erreur", variant: "danger", solid: true });
      }
    }
  },
  mounted() {
    this.fetchReportParams();
  }
};
</script>
